import React, { useMemo, useState } from "react";
import { IBoltComment } from "../../types/IBoltComment";
import { Avatar } from "@ucl/library";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faThumbtack,
  faTimes,
  faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import { InlineCommentEditor } from "../CommentEditor/InlineCommentEditor";
import moment from "moment";
import classNames from "classnames";

export interface CommentsListItemProps {
  comment: IBoltComment;
  canEdit?: boolean;
  canTogglePin?: boolean;
  canDelete?: boolean;
  onEdit?: (comment: IBoltComment) => Promise<void>;
  onTogglePin?: (comment: IBoltComment) => Promise<void>;
  onDelete?: (comment: IBoltComment) => Promise<void>;
  className?: string;
}

export const CommentsListItem: React.FC<CommentsListItemProps> = ({
  comment,
  canEdit = false,
  canTogglePin = false,
  canDelete = false,
  onEdit,
  onTogglePin,
  onDelete,
  className = "",
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const handleEdit = async (updatedComment: IBoltComment) => {
    if (!onEdit) return;

    setIsLoading(true);
    try {
      await onEdit(updatedComment);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to edit comment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePin = async () => {
    if (!onTogglePin) return;

    setIsLoading(true);
    try {
      await onTogglePin(comment);
    } catch (error) {
      console.error("Failed to pin/unpin comment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setIsLoading(true);
    try {
      await onDelete(comment);
    } catch (error) {
      console.error("Failed to delete comment:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAuthorName = () => {
    return comment.createdBy_AsName || comment.createdBy_AsRoleName || "";
  };

  const canUseEdit = useMemo(() => {
    return canEdit && onEdit;
  }, [canEdit, onEdit]);

  const canUseTogglePin = useMemo(() => {
    return canTogglePin && onTogglePin;
  }, [canTogglePin, onTogglePin]);

  const canUseDelete = useMemo(() => {
    return canDelete && onDelete;
  }, [canDelete, onDelete]);

  const canUseActions = useMemo(() => {
    return (canUseEdit || canUseTogglePin || canUseDelete) && !isEditing;
  }, [canUseEdit, canUseTogglePin, canUseDelete, isEditing]);

  return (
    <div
      className={classNames(`bolt-comments-list-item ${className}`, {
        "bolt-comments-list-item-edit-mode": isEditing,
      })}
    >
      <div className="bolt-comments-list-item-avatar">
        <Avatar
          size="large"
          fullName={getAuthorName()}
          avatarBgColor="#007bff"
          textColor="#ffffff"
        />
      </div>

      <div className="bolt-comments-list-item-content">
        <div className="bolt-comments-list-item-header">
          {comment.isPinned && (
            <span className="comment-pinned">
              <FontAwesomeIcon icon={faThumbtack} className="pin-icon" />
            </span>
          )}
          <div className="bolt-comments-list-item-author">
            <span className="author-name">{getAuthorName()}</span>
          </div>

          {canUseActions && (
            <div className="bolt-comments-list-item-actions">
              <button
                type="button"
                className="action-button action-button-menu"
                onClick={() => setShowActions(!showActions)}
                disabled={isLoading}
                aria-label="More actions"
              >
                <FontAwesomeIcon icon={faEllipsisH} />
              </button>

              {showActions && (
                <div className="action-menu">
                  {canUseEdit && (
                    <button
                      type="button"
                      className="action-menu-item"
                      onClick={() => {
                        setIsEditing(true);
                        setShowActions(false);
                      }}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      Edit
                    </button>
                  )}

                  {canUseTogglePin && (
                    <button
                      type="button"
                      className="action-menu-item"
                      onClick={handlePin}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faThumbtack} />
                      {comment.isPinned ? "Unpin" : "Pin"}
                    </button>
                  )}

                  {canUseDelete && (
                    <button
                      type="button"
                      className="action-menu-item action-menu-item-danger"
                      onClick={handleDelete}
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {isEditing ? (
          <div
            className={`bolt-comments-list-item bolt-comments-list-item-editing ${className}`}
          >
            <InlineCommentEditor
              comment={comment}
              onSave={handleEdit}
              onCancel={() => setIsEditing(false)}
              isLoading={isLoading}
            />
          </div>
        ) : (
          <div className="bolt-comments-list-item-message">
            {comment.isHtml ? (
              <div
                className="message-html"
                dangerouslySetInnerHTML={{
                  __html: comment.messageHtml || "",
                }}
              />
            ) : (
              <div className="message-text">{comment.messageText}</div>
            )}
          </div>
        )}
        <span className="comment-date">
          {comment.createdAt ? moment.utc(comment.createdAt.toString().endsWith('Z') ? comment.createdAt : comment.createdAt + 'Z').local().format("M/D/YYYY hh:mm A") : ''}
        </span>
      </div>
    </div>
  );
};
